import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import {
  Button,
  Input,
  login,
  router,
  SimpleDropdown,
  translate
} from "clavis-nxt-components-custdevtm";
import { ModuleConfigContext } from "../ModuleConfigContext/ModuleConfigContext";
import "./LoginStyle.scss";
import moment from "moment";

/** LocalStorage clavis language key. */
const LANGUAGE_KEY = "clavis.language";

/** Initialize language state variable value.
 *  @return {{name: string, value: string, initialize: boolean}} */
const initializeLanguage = () => {
  const storedLanguage = localStorage.getItem(LANGUAGE_KEY);

  if (storedLanguage) {
    return { name: storedLanguage, value: storedLanguage, initialize: true };
  }
  return { name: "HU", value: "HU", initialize: true };
};

/** Login button or enter key press function.
 *  @param {?string} username - Username input value.
 *  @param {?string} password - Password input value.
 *  @param {?Object} context - Context value.
 *  @param {function} navigateTo - Navigate function.
 *  @param {function} setLoading - Toggle loading variable value function.
 *  @param {function} setWarning - Set warning state variable value function. */
const onClickLogin = (
  username,
  password,
  smsOtp,
  context,
  navigateTo,
  setLoading,
  setWarning,
  setLoginBoxContent
) => {
  let domain = null;
  let _username = username;

  if (username && username.indexOf("\\") > -1) {
    domain = username.substring(0, username.indexOf("\\"));
    _username = username.substring(username.indexOf("\\") + 1);
  }
  login(
    _username,
    password,
    smsOtp,
    domain,
    resolveGatekeeperConfig(context)
  ).then(response => {
    if (response.success) {
      navigateTo("/nxt/custdevtm/welcome");
      //getNotification();
    } else if (response.error.error === "OneTimePasswordRequired") {
      setLoading(false);
      setLoginBoxContent("otp");
    } else {
      setLoading(false);
      setWarning({
        username: " ",
        password: `platform.Login.${response.error.error}`,
        smsOtp: `platform.Login.${response.error.error}`
      });
    }
  });
};

/** Get gatekeeper config from context.
 *  @param {?Object} context - Context object.
 *  @return {?Object} */
const resolveGatekeeperConfig = context =>
  context &&
  Object.hasOwn(context, "modules") &&
  Object.hasOwn(context.modules, "gatekeeper")
    ? context.modules.gatekeeper
    : null;

const Login = props => {
  const moduleConfigContext = useContext(ModuleConfigContext);
  const [allowLogin, setAllowLogin] = useState(false);
  const [language, setLanguage] = useState(initializeLanguage());
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState(null);
  const [username, setUsername] = useState(null);
  const [smsOtp, setSmsOtp] = useState(null);
  const [loginBoxContent, setLoginBoxContent] = useState("login"); //login/otp
  const [warning, setWarning] = useState({
    username: "",
    password: "",
    smsOtp: ""
  });
  const { t } = props;

  useEffect(() => {
    if (props.routerHistory && props.routerHistory.forceReload) {
      window.location.reload();
    }
    sessionStorage.removeItem("efed.userGroup");
    sessionStorage.removeItem("efed.userId");
    sessionStorage.removeItem("clavis.access_token");
    sessionStorage.removeItem("clavis.authorities");
    sessionStorage.removeItem("clavis.refresh_token");
    sessionStorage.removeItem("clavis.access_token_expires");
    sessionStorage.removeItem("clavis.username");
    console.log(`Stored language: ${localStorage.getItem(LANGUAGE_KEY)}`);
    setAllowLogin(true);
  }, []);

  useEffect(() => {
    if (!Object.hasOwn(language, "initialize")) {
      localStorage.setItem(LANGUAGE_KEY, language.value);
      window.location.reload();
    }
  }, [language]);

  /** Generate dropdown data. */
  const languages = useMemo(
    () => [{ name: "EN", value: "EN" }, { name: "HU", value: "HU" }],
    []
  );

  const memoizedOnClickLogin = useCallback(() => {
    setLoading(true);
    setWarning({ username: "", password: "", smsOtp: "" });
    localStorage.setItem(LANGUAGE_KEY, language.value);

    onClickLogin(
      username,
      password,
      smsOtp,
      moduleConfigContext,
      props.navigateTo,
      setLoading,
      setWarning,
      setLoginBoxContent
    );
  }, [username, password, smsOtp, moduleConfigContext, language]);

  const memoizedOnKeyDownLogin = useCallback(
    event => {
      if (event.key === "Enter" && !loading) {
        memoizedOnClickLogin();
      }
    },
    [loading, username, password, moduleConfigContext]
  );

  /** Memoized select language callback.
   *  @param {{name: string, value: string}} _language - Selected language. */
  const memoizedOnSelectLanguage = useCallback(
    _language => {
      if (_language.value !== language.value) {
        setLanguage(_language);
      }
    },
    [language]
  );

  if (!allowLogin || (props.routerHistory && props.routerHistory.forceReload)) {
    return <div />;
  }

  const environmentType = JSON.parse(localStorage.getItem("environment"))
    .environmentType;

  return (
    <div
      className="login-page page-header-background-color"
      onKeyDown={memoizedOnKeyDownLogin}
    >
      <div className="header-logo application-header-logo-66" />
      <div className="title huge-font-medium page-header-color">
        {t("platform.Login.login")}
        <SimpleDropdown
          data={languages}
          labelPrefix="platform.Login.language."
          labelProperty="name"
          onSelected={memoizedOnSelectLanguage}
          selected={language}
        />
      </div>
      {loginBoxContent === "login" && (
        <div
          className={
            "login-box interaction-background-color" +
            (warning.password === "" ? "" : " warning")
          }
        >
          <Input
            className="active"
            disabled={loading}
            id="name"
            label={t("platform.Login.username")}
            onChange={setUsername}
            value={username}
            warning={warning.username}
            autocomplete="new-username"
          />
          <Input
            disabled={loading}
            label={t("platform.Login.password")}
            onChange={setPassword}
            type="password"
            value={password}
            warning={t(warning.password)}
            autocomplete="new-password"
          />
          <Button loading={loading} onClick={memoizedOnClickLogin}>
            {t("platform.Login.login")}
          </Button>
        </div>
      )}
      {loginBoxContent === "otp" && (
        <div className="sms-otp-box interaction-background-color">
          <Input
            className="active"
            disabled={loading}
            id="smsotp"
            label={t("platform.Login.smsotp")}
            onChange={setSmsOtp}
            value={smsOtp}
            warning={warning.smsOtp}
          />
          <Button loading={loading} onClick={memoizedOnClickLogin}>
            {t("platform.Login.login")}
          </Button>
        </div>
      )}
      <div className="version page-header-color small-font-regular">
        {`${t("platform.Login.version")}: ${
          JSON.parse(localStorage.getItem("environment")).applicationVersion
        } (${process.env.REACT_APP_VERSION})`}
      </div>
      {environmentType !== "PROD" && (
        <div className="version page-header-color large-font-regular">
          {`${t("environment.type")}: ${environmentType}`}
        </div>
      )}
      <div className="footer-logo " />
      <div className="subtitle page-header-color small-font-regular ">
        {t("cdt.Login.subtitle")}
      </div>
      <div>
        <div className="copyright page-header-color small-font-regular ">
          © Copyright {moment().format("YYYY")} HUNFedezetkezelő Zrt.
        </div>
      </div>
    </div>
  );
};

export default router(translate()(Login));
